import * as Pipeline from '../core/utils/pipeline';
import { BaseComponent } from '../core/interfaces/BaseComponent';
import { Component, Element, Prop, Watch, h } from '@stencil/core';
import { Hyperlink, getHyperLinkObject } from '../core/interfaces/Hyperlink';
import { Orientation, Orientations } from '../core/types/globalTypes';
import { generateId } from '../core/utils/components';
import { isNullOrUndefined } from 'utils/collection';

/**
 * Card are flexible and extensible containers that can display a wide variety of content about a single subject.
 * Cards can contain typographical elements, images, and interactive elements like buttons and links.
 * Cards have no fixed width, so they’ll naturally fill the full width of its parent element.
 * Utility classes can be used to style the text alignment.
 * As default the card is vertical, but the orientation can be changed using the vertical or horizontal property.
 * @tag `<ads-card>`
 * @slot label - Label Area
 * @slot image - Image Area
 * @slot content - Content Area
 * @example
 * <ads-card has-open-border="true">
 *   <ads-card-content slot="content">
 *     <div class="u-padding-1">
 *       <h3 class="u-font-size-200% u-margin-top-1/2 u-margin-bottom-2 u-text-bold">Feature Card</h3>
 *       <div class="u-flex-1 u-margin-bottom-1">GigaOm explores the requirements for artificial intelligence to deliver on its promise of improving our lives through the devices.</div>
 *     </div>
 *   </ads-card-content>
 * </ads-card>
 */
@Component({
  tag: 'ads-card',
  styleUrl: 'ads-card.scss',
  shadow: true,
})
export class AdsCard implements BaseComponent {
  /**
   * @hidden
   */
  public component: string;

  constructor() {
    this.el.setAttribute('id', this.identifier || generateId('ads-card'));
    this.component = 'ads-card';
  }

  @Element() private el: HTMLAdsCardElement;

  /**
   * The unique identifier (optional)
   */
  @Prop({ attribute: 'id' }) identifier?: string;

  /**
   * Contains a URL (fragment).
   * If this property is set, an anchor tag will be rendered (optional)
   */
  @Prop({ reflect: true }) link?: Hyperlink;

  /**
   * Whether or not the card has an animation on hover (optional)
   * @default false
   */
  @Prop({ reflect: true }) hasHoverEffect? = false;

  /**
   * Whether or not the card has an open border (optional)
   * @default false
   */
  @Prop({ reflect: true }) hasOpenBorder = false;

  /**
   * The card orientation: "vertical" or "horizontal" (optional)
   * @default "vertical"
   */
  @Prop({ reflect: true }) orientation: Orientation = Orientations.VERTICAL;

  /**
   * Whether or not the card has the image and content reversed in order (optional)
   * @default false
   */
  @Prop({ reflect: true }) isReversed? = false;

  /**
   * Text to display as the aria-label (optional)
   */
  @Prop({ reflect: true }) srLabel?: string;
  /**
   * Handle identifier property changes
   * @param {string} newValue The new value for "identifier"
   * @hidden
   */
  @Watch('identifier')
  handleIdentifierChange(newValue: string): void {
    this.el.setAttribute('id', newValue || generateId('ads-card'));
  }

  protected componentDidLoad(): void {
    Pipeline.addComponent(this.el);
  }

  protected disconnectedCallback(): void {
    Pipeline.removeComponent(this.el);
  }

  protected render(): HTMLElement {
    const linkObj = getHyperLinkObject(this.link);

    const TagType = !isNullOrUndefined(linkObj) ? 'a' : 'div';

    // Generate the attributes list
    const attributes = {
      href: linkObj?.href,
      hrefLang: linkObj?.hreflang || null,
      rel: linkObj?.rel || null,
      target: linkObj?.target || null,
      onClick: linkObj?.onClick || null,
      'data-track': 'true',
      'data-track-value': this.srLabel,
    };

    const $adsCardLabel = this.el.querySelector('ads-card-label');
    const $adsCardImage = this.el.querySelector('ads-card-image');
    const $adsCardContent = this.el.querySelector('ads-card-content');

    const $labelEl = !isNullOrUndefined($adsCardLabel)
      ? ((
          <span class="c-card__label">
            <slot name="label" />
          </span>
        ) as HTMLElement)
      : null;
    const $imageEl = !isNullOrUndefined($adsCardImage)
      ? ((
          <span class="c-card__image">
            <slot name="image" />
          </span>
        ) as HTMLElement)
      : null;
    const $contentEl = !isNullOrUndefined($adsCardContent)
      ? ((
          <span class="c-card__content">
            <slot name="content" />
          </span>
        ) as HTMLElement)
      : null;

    const $slotContent = [$imageEl, $contentEl];
    this.isReversed ? $slotContent.reverse() : null;

    // Generate the class list
    const classes = ['c-card'];
    classes.push(this.hasHoverEffect ? 'has-hover-effect' : null);
    classes.push(!isNullOrUndefined($adsCardLabel) ? 'has-label' : null);
    classes.push(this.hasOpenBorder ? 'has-open-border' : null);
    classes.push(this.orientation === Orientations.HORIZONTAL ? 'is-horizontal' : null);

    return (
      <TagType class={classes.join(' ')} {...attributes}>
        {$labelEl}
        <div class="c-card__wrapper">{$slotContent}</div>
      </TagType>
    ) as HTMLElement;
  }
}
